import React, { useEffect, useState } from 'react';
import caseService from '../../services/caseService';

const CaseSettings = ({ caseId }) => {
  const [settings, setSettings] = useState(null);
  const [firmDefaults, setFirmDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Prepaid hours state
  const [prepaid, setPrepaid] = useState(null);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({
    hours: '',
    rate: '',
    description: ''
  });

  useEffect(() => {
    loadSettings();
    loadPrepaidStatus();
  }, [caseId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/cases/${caseId}/billing`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
        setFirmDefaults(data.firm_defaults);
      }
    } catch (err) {
      console.error('Failed to load billing settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`/api/cases/${caseId}/billing`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          billing_method: settings.billing_method,
          flat_fee_amount: settings.flat_fee_amount,
          flat_fee_description: settings.flat_fee_description,
          contingency_rate: settings.contingency_rate,
          contingency_base: settings.contingency_base,
          attorney_rate_override: settings.attorney_rate !== firmDefaults?.attorney_rate ? settings.attorney_rate : null,
          paralegal_rate_override: settings.paralegal_rate !== firmDefaults?.paralegal_rate ? settings.paralegal_rate : null,
          admin_rate_override: settings.admin_rate !== firmDefaults?.admin_rate ? settings.admin_rate : null,
          billing_notes: settings.billing_notes,
          retainer_amount: settings.retainer_amount,
          retainer_replenish_threshold: settings.retainer_replenish_threshold
        })
      });

      if (response.ok) {
        alert('Billing settings saved');
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      alert('Failed to save billing settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Prepaid hours functions
  const loadPrepaidStatus = async () => {
    try {
      const response = await fetch(`/api/cases/${caseId}/prepaid`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPrepaid(data);
      }
    } catch (err) {
      console.error('Failed to load prepaid status:', err);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await fetch(`/api/cases/${caseId}/prepaid/purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          hours: parseFloat(purchaseForm.hours),
          rate: parseFloat(purchaseForm.rate),
          description: purchaseForm.description || `Purchased ${purchaseForm.hours} hours`
        })
      });

      if (response.ok) {
        alert('Prepaid hours purchased successfully');
        setPurchaseForm({ hours: '', rate: '', description: '' });
        setShowPurchaseForm(false);
        loadPrepaidStatus();
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to purchase');
      }
    } catch (err) {
      alert('Failed to purchase prepaid hours: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h3 className="text-xl font-bold text-white mb-6">Case Billing Settings</h3>

      {/* Firm Defaults Reference */}
      {firmDefaults && (
        <div className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl p-4 mb-6">
          <p className="text-sm text-virgo-teal-400 mb-2">Firm Default Rates</p>
          <div className="flex flex-wrap gap-4 text-sm text-virgo-teal-300">
            <span>Attorney: <strong className="text-white">${firmDefaults.attorney_rate}/hr</strong></span>
            <span>Paralegal: <strong className="text-white">${firmDefaults.paralegal_rate}/hr</strong></span>
            <span>Admin: <strong className="text-white">${firmDefaults.admin_rate}/hr</strong></span>
          </div>
        </div>
      )}

      {/* Billing Method */}
      <div className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl p-4 mb-6">
        <h4 className="text-lg font-semibold text-white mb-4">Billing Method</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Method</label>
            <select
              value={settings?.billing_method || 'hourly'}
              onChange={(e) => handleChange('billing_method', e.target.value)}
              className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
            >
              <option value="hourly">Hourly</option>
              <option value="flat_fee">Flat Fee</option>
              <option value="contingency">Contingency</option>
              <option value="prepaid">Prepaid Hours</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>

          {settings?.billing_method === 'contingency' && (
            <>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Contingency Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={settings?.contingency_rate || ''}
                  onChange={(e) => handleChange('contingency_rate', parseFloat(e.target.value) || null)}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                  placeholder="33.33"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Contingency Base</label>
                <select
                  value={settings?.contingency_base || 'gross_recovery'}
                  onChange={(e) => handleChange('contingency_base', e.target.value)}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="gross_recovery">Gross Recovery</option>
                  <option value="net_recovery">Net Recovery</option>
                </select>
              </div>
            </>
          )}

          {settings?.billing_method === 'flat_fee' && (
            <>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Flat Fee Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-virgo-teal-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={settings?.flat_fee_amount || ''}
                    onChange={(e) => handleChange('flat_fee_amount', parseFloat(e.target.value) || null)}
                    className="w-full pl-8 pr-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                    placeholder="5000.00"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Fee Description</label>
                <input
                  type="text"
                  value={settings?.flat_fee_description || ''}
                  onChange={(e) => handleChange('flat_fee_description', e.target.value)}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                  placeholder="Description of flat fee scope..."
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Rate Overrides */}
      <div className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl p-4 mb-6">
        <h4 className="text-lg font-semibold text-white mb-2">Rate Overrides</h4>
        <p className="text-sm text-virgo-teal-500 mb-4">Leave blank to use firm defaults</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Attorney Rate</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-virgo-teal-400">$</span>
              <input
                type="number"
                step="0.01"
                value={settings?.attorney_rate || ''}
                onChange={(e) => handleChange('attorney_rate', parseFloat(e.target.value) || firmDefaults?.attorney_rate)}
                className="w-full pl-8 pr-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                placeholder={firmDefaults?.attorney_rate?.toString() || '350'}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Paralegal Rate</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-virgo-teal-400">$</span>
              <input
                type="number"
                step="0.01"
                value={settings?.paralegal_rate || ''}
                onChange={(e) => handleChange('paralegal_rate', parseFloat(e.target.value) || firmDefaults?.paralegal_rate)}
                className="w-full pl-8 pr-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                placeholder={firmDefaults?.paralegal_rate?.toString() || '150'}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Admin Rate</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-virgo-teal-400">$</span>
              <input
                type="number"
                step="0.01"
                value={settings?.admin_rate || ''}
                onChange={(e) => handleChange('admin_rate', parseFloat(e.target.value) || firmDefaults?.admin_rate)}
                className="w-full pl-8 pr-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                placeholder={firmDefaults?.admin_rate?.toString() || '75'}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Retainer */}
      <div className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl p-4 mb-6">
        <h4 className="text-lg font-semibold text-white mb-4">Retainer / Trust Account</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Retainer Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-virgo-teal-400">$</span>
              <input
                type="number"
                step="0.01"
                value={settings?.retainer_amount || ''}
                onChange={(e) => handleChange('retainer_amount', parseFloat(e.target.value) || 0)}
                className="w-full pl-8 pr-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                placeholder="0.00"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Replenish Threshold</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-virgo-teal-400">$</span>
              <input
                type="number"
                step="0.01"
                value={settings?.retainer_replenish_threshold || ''}
                onChange={(e) => handleChange('retainer_replenish_threshold', parseFloat(e.target.value) || null)}
                className="w-full pl-8 pr-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                placeholder="Alert when balance below..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Billing Notes */}
      <div className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl p-4 mb-6">
        <h4 className="text-lg font-semibold text-white mb-4">Billing Notes</h4>
        <textarea
          value={settings?.billing_notes || ''}
          onChange={(e) => handleChange('billing_notes', e.target.value)}
          rows={4}
          className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
          placeholder="Special billing arrangements, discounts, etc..."
        />
      </div>

      {/* Prepaid Hours */}
      <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-white">Prepaid Hours</h4>
          {!prepaid?.enabled && (
            <button
              onClick={() => setShowPurchaseForm(true)}
              className="px-3 py-1 text-sm bg-cyan-500/20 text-cyan-300 rounded border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors"
            >
              + Add Prepaid Package
            </button>
          )}
        </div>

        {prepaid?.enabled ? (
          <>
            {/* Balance Display */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className={`rounded-lg p-4 text-center ${
                prepaid.balance_dollars < 0
                  ? 'bg-red-500/10 border border-red-500/30'
                  : 'bg-virgo-navy-800/80'
              }`}>
                <p className="text-sm text-virgo-teal-400">
                  {prepaid.balance_dollars < 0 ? 'Hours Overage' : 'Hours Remaining'}
                </p>
                <p className={`text-3xl font-bold ${
                  prepaid.balance_dollars < 0 ? 'text-red-400' : 'text-green-400'
                }`}>
                  {prepaid.balance_dollars < 0
                    ? Math.abs(prepaid.hours_remaining).toFixed(1)
                    : prepaid.hours_remaining}
                </p>
                <p className="text-xs text-virgo-teal-500">
                  {prepaid.balance_dollars < 0
                    ? 'to be billed'
                    : `at $${prepaid.purchased_rate}/hr`}
                </p>
              </div>
              <div className={`rounded-lg p-4 text-center ${
                prepaid.balance_dollars < 0
                  ? 'bg-red-500/10 border border-red-500/30'
                  : 'bg-virgo-navy-800/80'
              }`}>
                <p className="text-sm text-virgo-teal-400">
                  {prepaid.balance_dollars < 0 ? 'Overage Amount' : 'Balance'}
                </p>
                <p className={`text-3xl font-bold ${
                  prepaid.balance_dollars < 0 ? 'text-red-400' : 'text-white'
                }`}>
                  ${Math.abs(prepaid.balance_dollars || 0).toFixed(2)}
                </p>
                <p className="text-xs text-virgo-teal-500">
                  {prepaid.balance_dollars < 0 ? 'to invoice' : 'dollar value'}
                </p>
              </div>
              <div className="bg-virgo-navy-800/80 rounded-lg p-4 text-center">
                <p className="text-sm text-virgo-teal-400">Total Purchased</p>
                <p className="text-3xl font-bold text-virgo-teal-300">{prepaid.purchased_hours}</p>
                <p className="text-xs text-virgo-teal-500">hours originally</p>
              </div>
            </div>

            {/* Overage Warning Banner */}
            {prepaid.balance_dollars < 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-300">
                  <strong>Prepaid hours exhausted.</strong> Additional time of ${Math.abs(prepaid.balance_dollars).toFixed(2)}
                  ({Math.abs(prepaid.hours_remaining).toFixed(1)} hours) is ready to be invoiced.
                  Purchase additional hours to reset the balance.
                </p>
              </div>
            )}

            {/* How it works */}
            <div className="bg-virgo-navy-800/50 rounded-lg p-3 mb-4">
              <p className="text-xs text-virgo-teal-400">
                <strong>How it works:</strong> Time is deducted at the worker's rate. If your paralegal ($150/hr) works 1 hour,
                only $150 is deducted from the balance, preserving more hours at your rate (${prepaid.purchased_rate}/hr).
              </p>
            </div>

            {/* Add more hours */}
            {!showPurchaseForm ? (
              <button
                onClick={() => setShowPurchaseForm(true)}
                className="w-full px-4 py-2 text-sm bg-cyan-500/20 text-cyan-300 rounded border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors"
              >
                + Purchase Additional Hours
              </button>
            ) : null}

            {/* Transaction History */}
            {prepaid.transactions?.length > 0 && (
              <div className="mt-4">
                <h5 className="text-sm font-semibold text-virgo-teal-400 mb-2">Recent Transactions</h5>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {prepaid.transactions.map(t => (
                    <div key={t.id} className="flex items-center justify-between bg-virgo-navy-800/50 rounded px-3 py-2 text-sm">
                      <div>
                        <span className={`px-2 py-0.5 rounded text-xs mr-2 ${
                          t.type === 'purchase' ? 'bg-green-500/20 text-green-300' :
                          t.type === 'deduction' ? 'bg-amber-500/20 text-amber-300' :
                          'bg-blue-500/20 text-blue-300'
                        }`}>
                          {t.type}
                        </span>
                        <span className="text-virgo-teal-300">{t.description}</span>
                      </div>
                      <div className="text-right">
                        <span className={t.amount >= 0 ? 'text-green-400' : 'text-red-400'}>
                          {t.amount >= 0 ? '+' : ''}{t.amount?.toFixed(2)}
                        </span>
                        <span className="text-virgo-teal-500 text-xs ml-2">
                          ({t.hours_equivalent_after}hrs)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-virgo-teal-500">
            Set up prepaid hours to allow clients to purchase blocks of your time in advance.
            Lower-rate work (paralegal, admin) stretches the purchased hours further.
          </p>
        )}

        {/* Purchase Form */}
        {showPurchaseForm && (
          <form onSubmit={handlePurchase} className="mt-4 p-4 bg-virgo-navy-800/80 rounded-lg border border-cyan-500/20">
            <h5 className="text-sm font-semibold text-white mb-3">Purchase Prepaid Hours</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Hours</label>
                <input
                  type="number"
                  step="0.1"
                  required
                  value={purchaseForm.hours}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, hours: e.target.value })}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                  placeholder="10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Rate ($/hr)</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-virgo-teal-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={purchaseForm.rate}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, rate: e.target.value })}
                    className="w-full pl-8 pr-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                    placeholder={firmDefaults?.attorney_rate?.toString() || '350'}
                  />
                </div>
              </div>
            </div>

            {purchaseForm.hours && purchaseForm.rate && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-sm text-green-300">
                  Total: <strong>${(parseFloat(purchaseForm.hours || 0) * parseFloat(purchaseForm.rate || 0)).toFixed(2)}</strong>
                  {' '}for {purchaseForm.hours} hours at ${purchaseForm.rate}/hr
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowPurchaseForm(false);
                  setPurchaseForm({ hours: '', rate: '', description: '' });
                }}
                className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all"
              >
                {saving ? 'Processing...' : 'Purchase Hours'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 font-medium shadow-lg shadow-cyan-500/30 transition-all disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save Billing Settings'}
      </button>
    </div>
  );
};

export default CaseSettings;
